import React, { useEffect, useState } from "react";
import { MdCallSplit, MdLock } from "react-icons/md";

import CircularLoader from "../../../../../../../Components/CircularLoader/CircularLoader";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import { fetchRuns } from "../../../../../../../services/ShipperServices/DeliveryRoutes/deliveryRuns.services";
import SheetDialog from "./SheetDialog";
import SheetChip from "./SheetChip";
import { deliveryAddress } from "./routeSheet";
import {
  driverName,
  isLockedRun,
  isOpenRun,
  lockedByText,
  RUN_MAX_STOPS,
  runDay,
  runTitle,
  stopsText,
} from "./runs";

const FIELD =
  "w-full rounded-[6px] border-[1px] border-ui-border-strong bg-ui-surface px-[9px] py-[6px] text-[12.5px] text-ui-text focus:border-brand focus:outline-none";
const LABEL =
  "text-[10px] font-semibold uppercase tracking-[0.06em] text-ui-text-muted";
const LIST =
  "m-[0px] list-none overflow-hidden rounded-[7px] border-[1px] border-ui-border bg-ui-surface p-[0px]";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const customerName = (shipping) =>
  [shipping?.first_name, shipping?.last_name]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");

/**
 * Move stops: the stops ticked on the route sheet (`orders`, none with an
 * outcome yet) leave `run` for a new run (named "<run> (split)", with the
 * run's date, driver and start, all of which can change) or the end of
 * another open run. Locked runs, and runs that would pass RUN_MAX_STOPS, are
 * listed but can't be picked. The sheet shows it only while it's open, so
 * each time starts from the run as it is then. `onMove(target)` moves them
 * (target: { run: { name, run_date, driver_id, start_warehouse_id } } or
 * { toRunId }) and resolves to why it couldn't, or "" once they moved.
 * `blocked` says why they can't move now (the run was locked meanwhile,
 * say). `drivers` and `locations` fill the new run's lists; `startId` is the
 * sheet's start location.
 */
const MoveStopsDialog = ({
  run,
  orders = [],
  drivers = [],
  locations = [],
  startId = null,
  blocked = "",
  busy = false,
  onMove,
  onClose,
}) => {
  const [to, setTo] = useState("new");
  const [name, setName] = useState(() => `${runTitle(run)} (split)`);
  const [date, setDate] = useState(() =>
    String(run?.run_date ?? "").slice(0, 10),
  );
  const [driverId, setDriverId] = useState(() =>
    run?.driver_id ? String(run.driver_id) : "",
  );
  const [start, setStart] = useState(() => (startId ? String(startId) : ""));
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [toRunId, setToRunId] = useState(null);
  const [error, setError] = useState("");

  // The other open runs, read once as it opens.
  useEffect(() => {
    let cancelled = false;
    fetchRuns().then((response) => {
      if (cancelled) return;
      const list = response?.data?.result?.data;
      setFailed(Boolean(response?.error) || !Array.isArray(list));
      setRuns(
        Array.isArray(list)
          ? list.filter((other) => isOpenRun(other) && other.id !== run?.id)
          : [],
      );
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // Once, for the run it opened on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const count = orders.length;
  const room = (other) =>
    Number(other?.stops_count ?? 0) + count <= RUN_MAX_STOPS;
  const chosen = runs.find(
    (other) => other.id === toRunId && !isLockedRun(other) && room(other),
  );
  const ready =
    count > 0 && !blocked && (to === "new" ? Boolean(date) : Boolean(chosen));

  const move = async () => {
    setError("");
    const why = await onMove(
      to === "new"
        ? {
            run: {
              name: name.trim() || null,
              run_date: date,
              driver_id: driverId ? Number(driverId) : null,
              start_warehouse_id: start ? Number(start) : null,
            },
          }
        : { toRunId: chosen.id },
    );
    if (why) setError(why);
  };

  const knownDriver = drivers.some(
    (driver) => String(driver.id) === String(run?.driver_id),
  );

  return (
    <SheetDialog
      open
      labelId="moveStopsTitle"
      title={`Move ${plural(count, "stop")}`}
      subtitle={`${count === 1 ? "It leaves" : "They leave"} ${runTitle(run)}`}
      icon={MdCallSplit}
      widthClass="max-w-[600px]"
      closeDisabled={busy}
      onClose={onClose}
      footer={
        <>
          <BoltButton
            type="button"
            variant="outline"
            size="sm"
            label="Cancel"
            onClick={onClose}
            disabled={busy}
          />
          <BoltButton
            type="button"
            variant="primary"
            size="sm"
            label={busy ? "Moving…" : `Move ${plural(count, "stop")}`}
            onClick={move}
            disabled={busy || !ready}
          />
        </>
      }
    >
      <ul
        aria-label="Stops to move"
        className={`${LIST} max-h-[132px] overflow-auto`}
      >
        {orders.map((order) => (
          <li
            key={order.id}
            className="truncate border-b-[1px] border-ui-border-subtle px-[12px] py-[6px] last:border-b-0"
          >
            <b>#{order.id}</b>
            {[
              customerName(order?.order_shipping_address_details),
              deliveryAddress(order?.order_shipping_address_details),
            ]
              .filter(Boolean)
              .map((part) => ` · ${part}`)
              .join("")}
          </li>
        ))}
      </ul>

      <div
        role="radiogroup"
        aria-label="Move them to"
        className="flex flex-wrap gap-[6px]"
      >
        {[
          ["new", "A new run"],
          ["existing", "Another open run"],
        ].map(([value, label]) => (
          <label
            key={value}
            className={`flex cursor-pointer items-center gap-[6px] rounded-full border-[1px] px-[11px] py-[5px] text-[12px] ${
              to === value
                ? "border-brand bg-brand-light font-semibold text-brand"
                : "border-ui-border bg-ui-surface text-ui-text-muted hover:text-ui-text"
            }`}
          >
            <input
              type="radio"
              name="moveStopsTo"
              value={value}
              checked={to === value}
              onChange={() => setTo(value)}
              className="h-[14px] w-[14px] accent-brand"
            />
            {label}
          </label>
        ))}
      </div>

      {to === "new" ? (
        <section className="grid gap-[10px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface p-[12px] sm:grid-cols-2">
          <label className="grid gap-[4px] sm:col-span-2">
            <span className={LABEL}>Name</span>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className={FIELD}
            />
          </label>
          <label className="grid gap-[4px]">
            <span className={LABEL}>Date</span>
            <input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              className={FIELD}
            />
          </label>
          <label className="grid gap-[4px]">
            <span className={LABEL}>Driver</span>
            <select
              value={driverId}
              onChange={(event) => setDriverId(event.target.value)}
              className={FIELD}
            >
              <option value="">No driver yet</option>
              {run?.driver_id && !knownDriver && (
                <option value={String(run.driver_id)}>
                  {driverName(run.driver) || `User #${run.driver_id}`}
                </option>
              )}
              {drivers.map((driver) => (
                <option key={driver.id} value={String(driver.id)}>
                  {driverName(driver) || `User #${driver.id}`}
                  {driver.role === "Driver" ? " · Driver" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-[4px] sm:col-span-2">
            <span className={LABEL}>Start and end at</span>
            <select
              value={start}
              onChange={(event) => setStart(event.target.value)}
              className={FIELD}
            >
              {!locations.length && <option value="">No location</option>}
              {locations.map((location) => (
                <option key={location.id} value={String(location.id)}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
        </section>
      ) : loading ? (
        <div className="grid min-h-[100px] place-items-center">
          <CircularLoader size={22} />
        </div>
      ) : failed ? (
        <p className="m-[0px] rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg px-[12px] py-[10px] text-[12px] text-state-danger-text">
          Couldn't load the runs. Close and try again, or move them to a new
          run.
        </p>
      ) : runs.length === 0 ? (
        <p className="m-[0px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface px-[12px] py-[14px] text-[12px] text-ui-text-muted">
          No other open runs. Move them to a new run instead.
        </p>
      ) : (
        <ul role="radiogroup" aria-label="Open runs" className={LIST}>
          {runs.map((other) => {
            const locked = isLockedRun(other);
            const full = !room(other);
            const blocked = locked || full;
            return (
              <li
                key={other.id}
                className="border-b-[1px] border-ui-border-subtle last:border-b-0"
              >
                <label
                  className={`flex min-h-[44px] items-center gap-[10px] px-[12px] py-[8px] ${
                    blocked
                      ? "cursor-not-allowed"
                      : "cursor-pointer hover:bg-ui-surface-sunken"
                  }`}
                >
                  <input
                    type="radio"
                    name="moveStopsToRun"
                    checked={toRunId === other.id}
                    disabled={blocked}
                    onChange={() => setToRunId(other.id)}
                    className="h-[16px] w-[16px] shrink-0 accent-brand disabled:opacity-40"
                  />
                  <div className="grid min-w-[0px] flex-1 gap-[1px]">
                    <span
                      className={`truncate font-semibold ${blocked ? "text-ui-text-muted" : ""}`}
                    >
                      {runTitle(other)}
                    </span>
                    <span className="truncate text-ui-text-muted">
                      {[
                        runDay(other.run_date),
                        driverName(other.driver) || "No driver",
                        stopsText(other.stops_count),
                      ].join(" · ")}
                    </span>
                    {locked ? (
                      <span className="truncate text-[11px] text-state-warning-text">
                        {lockedByText(other)}. Unlock it to move stops to it.
                      </span>
                    ) : (
                      full && (
                        <span className="truncate text-[11px] text-ui-text-muted">
                          A run takes up to {RUN_MAX_STOPS} stops: no room for{" "}
                          {count === 1 ? "this one" : `these ${count}`}.
                        </span>
                      )
                    )}
                  </div>
                  {locked && (
                    <SheetChip tone="warning">
                      <MdLock
                        aria-hidden="true"
                        className="h-[11px] w-[11px]"
                      />
                      Locked
                    </SheetChip>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {to === "existing" && chosen && (
        <p className="m-[0px] text-[11.5px] text-ui-text-muted">
          {count === 1 ? "The stop goes" : "The stops go"} at the end of{" "}
          {runTitle(chosen)}.
        </p>
      )}

      {error ? (
        <p
          role="alert"
          className="m-[0px] rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg px-[12px] py-[8px] text-[12px] text-state-danger-text"
        >
          {error}
        </p>
      ) : (
        blocked && (
          <p className="m-[0px] text-[12px] text-ui-text-muted">{blocked}</p>
        )
      )}
    </SheetDialog>
  );
};

export default MoveStopsDialog;
